import { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './prezzi.scss';

gsap.registerPlugin(ScrollTrigger);

const pricingPlans = [
  {
    title: 'Sito Vetrina',
    price: '€1.500 - €2.500',
    color: 'primary',
    features: [
      'Design personalizzato',
      '3-5 pagine responsive',
      'Form contatti',
      'Ottimizzazione SEO base',
      'Integrazione Google Maps',
      'Tempi: 2-3 settimane'
    ]
  },
  {
    title: 'Sito Aziendale',
    price: '€3.000 - €8.000',
    color: 'secondary',
    features: [
      'UI/UX design avanzato',
      '6-12 pagine responsive',
      'Area news/blog',
      'SEO completo',
      'Analytics e pixel tracking',
      'Multilingua (opzionale)',
      'Tempi: 4-6 settimane'
    ]
  },
  {
    title: 'Web App Custom',
    price: 'Da €5.000',
    color: 'primary',
    features: [
      'Applicazione su misura',
      'Dashboard amministrazione',
      'Database e API',
      'Autenticazione utenti',
      'Features personalizzate',
      'Scalabilità garantita',
      'Tempi: 8-12+ settimane'
    ]
  }
];

const maintenancePlans = [
  {
    title: 'Basic',
    price: '€100/mese',
    color: 'primary',
    features: [
      'Hosting e dominio',
      'SSL certificato',
      'Backup settimanali',
      'Aggiornamenti sicurezza',
      'Supporto email',
      '1 modifica contenuti/mese'
    ]
  },
  {
    title: 'Professional',
    price: '€250/mese',
    color: 'secondary',
    features: [
      'Tutto del Basic',
      'Backup giornalieri',
      'Monitoraggio uptime 24/7',
      'Report mensili analytics',
      'Supporto prioritario',
      '3 modifiche contenuti/mese',
      'Ottimizzazione SEO continua'
    ]
  },
  {
    title: 'Enterprise',
    price: '€500/mese',
    color: 'primary',
    features: [
      'Tutto del Professional',
      'Gestione completa contenuti',
      'Server dedicato',
      'Supporto telefonico',
      'Modifiche illimitate',
      'Consulenza strategica mensile'
    ]
  }
];

const faqData = [
  {
    question: "Quanto costa un sito web professionale?",
    answer: "Il costo di un sito web professionale varia in base alle esigenze specifiche del progetto. Un sito vetrina base parte da circa €1.500-€2.500, mentre un sito aziendale completo con funzionalità avanzate può andare dai €3.000 ai €8.000. I progetti personalizzati con web app o e-commerce hanno costi superiori. Offro sempre un preventivo gratuito dopo aver analizzato le tue necessità."
  },
  {
    question: "Cosa include il servizio di creazione sito web?",
    answer: "Il servizio completo include: progettazione UI/UX personalizzata, sviluppo front-end responsive con React, ottimizzazione SEO on-page, integrazione form contatti, ottimizzazione performance, test su tutti i dispositivi, formazione base per la gestione autonoma e supporto post-lancio."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un sito web?",
    answer: "I tempi di sviluppo dipendono dalla complessità del progetto. Un sito vetrina si completa in 2-3 settimane, un sito aziendale richiede 4-6 settimane, mentre progetti più complessi (e-commerce, web app) possono richiedere 8-12 settimane."
  },
  {
    question: "Offri servizi di restyling per siti web esistenti?",
    answer: "Sì, offro servizi di restyling e modernizzazione per siti web esistenti. Analizzo il sito attuale, identifico le aree di miglioramento (design, UX, performance, SEO) e propongo soluzioni moderne. Il restyling può partire da €1.200 per interventi base fino a €5.000+ per rifacimenti completi."
  },
  {
    question: "Il sito sarà ottimizzato per mobile?",
    answer: "Assolutamente sì. Tutti i siti che realizzo sono completamente responsive e ottimizzati per ogni dispositivo. Utilizzo un approccio mobile-first, garantendo che l'esperienza utente sia perfetta su qualsiasi schermo."
  },
  {
    question: "Fornisci supporto dopo il lancio del sito?",
    answer: "Sì, offro supporto post-lancio per garantire che tutto funzioni perfettamente. Include: 30 giorni di assistenza gratuita per bug fixing, formazione sulla gestione del sito, supporto tecnico via email. Successivamente puoi scegliere tra pacchetti di manutenzione mensili o interventi on-demand."
  }
];

function Prezzi() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Video parallax
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cards animation
    const cards = document.querySelectorAll('.pricing-card, .faq-item');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Quanto Costa un Sito Web nel 2025? Prezzi Reali: €1.500 - €8.000</title>
        <meta name="description" content="Sito vetrina da €1.500 | Sito aziendale €3.000-€8.000 | Web app da €5.000. Prezzi reali 2025 tutto incluso: design, sviluppo, SEO. Preventivo gratuito in 24h." />
        <meta name="keywords" content="quanto costa sito web, prezzi siti web, preventivo sito web, costo creazione sito, quanto costa sito web professionale, prezzi web designer" />
        <meta property="og:title" content="Quanto Costa un Sito Web nel 2025? Prezzi Reali: €1.500 - €8.000" />
        <meta property="og:description" content="Sito vetrina da €1.500 | Sito aziendale €3.000-€8.000 | Web app da €5.000. Prezzi reali tutto incluso." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/prezzi" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/prezzi" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="prezzi-page">
        {/* Hero Section */}
        <section className="prezzi-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Trasparenza
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">Prezzi e</span>
                  <span className="hero-line"><span className="gradient-text">Preventivi</span></span>
                </h1>

                <p className="hero-description">
                  Prezzi chiari e trasparenti per ogni progetto.
                  Nessuna sorpresa, solo qualità e professionalità.
                </p>

                <div className="hero-cta-group">
                  <a
                    href="#prezzi"
                    className="hero-cta"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('prezzi')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Vedi i prezzi
                    <span className="arrow">↓</span>
                  </a>
                  <a
                    href="#faq"
                    className="hero-cta secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    FAQ
                    <span className="arrow">↓</span>
                  </a>
                </div>
              </div>

              <div className="hero-preview">
                <div className="preview-card card-1">
                  <span className="preview-tag">Base</span>
                  <span className="preview-title">Sito Vetrina</span>
                  <span className="preview-price">da €1.500</span>
                </div>
                <div className="preview-card card-2">
                  <span className="preview-tag">Popular</span>
                  <span className="preview-title">Sito Aziendale</span>
                  <span className="preview-price">da €3.000</span>
                </div>
                <div className="preview-card card-3">
                  <span className="preview-tag">Custom</span>
                  <span className="preview-title">Web App</span>
                  <span className="preview-price">da €5.000</span>
                </div>
              </div>
            </div>
          </Container>

          <div className="scroll-indicator">
            <div className="scroll-line"></div>
            <span>Scroll</span>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="pricing-section" id="prezzi">
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">01</span>
                Prezzi Indicativi
              </h2>
            </div>

            <div className="pricing-grid">
              {pricingPlans.map((plan, index) => (
                <div key={index} className={`pricing-card ${plan.color}`}>
                  <h3 className="plan-title">{plan.title}</h3>
                  <div className="plan-price">{plan.price}</div>
                  <ul className="plan-features">
                    {plan.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="cta-center">
              <button
                className="hero-cta"
                onClick={() => navigateWithTransition('/contatti')}
              >
                <span className="cta-text">Richiedi Preventivo Gratuito</span>
                <span className="arrow">→</span>
              </button>
            </div>
          </Container>
        </section>

        {/* Maintenance Section */}
        <section className="maintenance-section">
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">02</span>
                Canoni di Mantenimento
              </h2>
            </div>

            <div className="pricing-grid">
              {maintenancePlans.map((plan, index) => (
                <div key={index} className={`pricing-card ${plan.color}`}>
                  <h3 className="plan-title">{plan.title}</h3>
                  <div className="plan-price">{plan.price}</div>
                  <ul className="plan-features">
                    {plan.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* FAQ Section */}
        <section className="faq-section" id="faq" style={{ backgroundImage: "url('/sfondi/sfondo2.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">03</span>
                Domande Frequenti
              </h2>
            </div>

            <div className="faq-grid">
              {faqData.map((faq, index) => (
                <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    {faq.question}
                    <span className="faq-icon">+</span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Prezzi;
